import { useEffect, useState } from 'react';

/**
 * A gap between the layout and the visual viewport smaller than this is not a
 * keyboard — a collapsing browser URL bar is ~50px and an iOS input accessory
 * bar alone ~45px, while the smallest software keyboard (landscape, plus that
 * bar) is comfortably over 200px.
 */
const KEYBOARD_MIN_HEIGHT = 120;

/**
 * iOS reports the visual viewport through the keyboard and rotation
 * animations and does not always correct itself afterwards — measured on
 * iPhone / iOS 18.7 inside the native host: `visualViewport.height` sat at
 * 426 against an `innerHeight` of 849 with no keyboard anywhere on screen,
 * and stayed there. So every event also schedules a re-read once the
 * animation is over, and a stale answer heals instead of sticking.
 */
const SETTLE_MS = 400;

function keyboardIsOpen(): boolean {
  const vv = window.visualViewport;
  if (!vv) return false;
  // `clientHeight` is the LAYOUT viewport, which WebKit does not shrink for
  // the keyboard (`interactive-widget` is still unimplemented there —
  // webkit.org/b/259770), so the gap between the two is the keyboard.
  return document.documentElement.clientHeight - vv.height > KEYBOARD_MIN_HEIGHT;
}

/**
 * Whether a software keyboard is currently covering part of the frame.
 *
 * Deliberately a single boolean and NOT the visible rectangle. An overlay
 * that takes its painted box from `visualViewport` inherits every one of the
 * platform's reporting bugs: a stale height clipped the sign-in card and left
 * the bare page showing below it after a rotation, on a device where the
 * value never updated. A boolean can be wrong too, but the worst it does is
 * centre a dialog that should have been top-aligned.
 *
 * Consumers must keep painting across the whole viewport regardless of this,
 * and use it only to decide where the CONTENT sits.
 */
export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(keyboardIsOpen);

  useEffect(() => {
    let settle: ReturnType<typeof setTimeout> | undefined;
    const sync = () => setOpen(keyboardIsOpen());
    const onChange = () => {
      sync();
      clearTimeout(settle);
      settle = setTimeout(sync, SETTLE_MS);
    };
    // The keyboard may have opened between the render that mounted this and
    // this effect — re-read rather than trusting the initial state.
    onChange();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', onChange);
    // Rotation and split-view resizes do not always reach the visual viewport.
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => {
      clearTimeout(settle);
      vv?.removeEventListener('resize', onChange);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
    };
  }, []);

  return open;
}
